import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import SellerLayout from "@/components/SellerLayout";
import { PageHeader, EmptyState } from "@/components/ui-system";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Zap, Check, Info, Loader2, BadgeCheck, ShieldAlert } from "lucide-react";
import { can } from "@/lib/permissions";

const errDetail = (e, fb) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d) && d[0]?.msg) return d[0].msg;
  return e?.response?.data?.message || fb;
};

const B2CEarlyCod = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const canOptIn = can(user, "b2c.early_cod:opt_in");

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyTier, setBusyTier] = useState(null);

  const fetchView = useCallback(async () => {
    setLoading(true);
    try {
      const { data: res } = await axios.get(`${API}/b2c/seller/early-cod`);
      setData(res);
    } catch (e) {
      toast.error(errDetail(e, "Failed to load Early COD"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchView(); }, [fetchView]);

  const optIn = async (tier) => {
    setBusyTier(tier);
    try {
      const { data: res } = await axios.post(`${API}/b2c/seller/early-cod/opt-in`, { tier });
      setData(res);
      toast.success(`Early COD ${tier} activated — your COD now remits faster.`);
    } catch (e) {
      toast.error(errDetail(e, "Could not activate this plan"));
    } finally {
      setBusyTier(null);
    }
  };

  const optOut = async () => {
    setBusyTier("__out__");
    try {
      const { data: res } = await axios.post(`${API}/b2c/seller/early-cod/opt-out`);
      setData(res);
      toast.success("Early COD cancelled — back to the standard remittance cycle.");
    } catch (e) {
      toast.error(errDetail(e, "Could not cancel"));
    } finally {
      setBusyTier(null);
    }
  };

  const currentTier = data?.current?.tier || null;
  const gst = data?.gst_pct ?? 18;

  return (
    <SellerLayout user={user} onLogout={onLogout}>
      <div className="space-y-6 w-full max-w-4xl">
        <PageHeader
          title="Early COD"
          subtitle="Get your COD money faster than the standard cycle, for a small fee"
        />

        {loading ? (
          <div className="py-16 text-center"><Loader2 className="w-8 h-8 mx-auto animate-spin text-slate-300" /></div>
        ) : !data?.feature_enabled ? (
          <Card className="border-0 shadow-sm"><CardContent className="p-0">
            <EmptyState icon={Zap} title="Early COD isn't available right now" description="This feature isn't enabled for your account yet. Check back later or contact your account manager." />
          </CardContent></Card>
        ) : (
          <>
            <div className="flex items-start gap-3 rounded-lg bg-slate-50 border border-slate-200 p-3 text-sm text-slate-600">
              <Info className="w-5 h-5 text-slate-400 shrink-0 mt-0.5" />
              <span>
                Normally your COD is remitted in ~<strong>{data.standard_cycle_days} days</strong> after delivery. With Early COD you choose a faster <strong>D+N</strong> plan — money in N days after delivery — for a fee that's a % of the COD amount (plus {gst}% GST). The fee is <strong>deducted from each COD payout</strong> (you receive COD − fee − GST); your wallet is never touched.
              </span>
            </div>

            {!data.eligible && (
              <div className="flex items-start gap-3 rounded-lg bg-amber-50 border border-amber-200 p-4 text-sm">
                <ShieldAlert className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                <div className="text-amber-800">
                  <p className="font-semibold">Approved KYC required</p>
                  <p>Early COD needs an approved KYC with your bank details on file (that's where the COD is paid).{" "}
                    <button className="underline font-medium" onClick={() => navigate("/seller/b2c/kyc")}>Complete KYC</button>
                  </p>
                </div>
              </div>
            )}

            {currentTier && (
              <div className="flex items-center justify-between rounded-lg bg-brand-bg border border-brand/20 p-4">
                <div className="flex items-center gap-2 text-sm">
                  <BadgeCheck className="w-5 h-5 text-brand" />
                  <span>You're on <strong className="text-brand">Early COD {currentTier}</strong>. COD remits ~{(data.plans.find((p) => p.tier === currentTier)?.days)} day(s) after delivery.</span>
                </div>
                <Button variant="outline" size="sm" className="text-red-600 hover:bg-red-50" onClick={optOut} disabled={busyTier === "__out__"} data-testid="early-cod-cancel">
                  {busyTier === "__out__" ? <Loader2 className="w-4 h-4 animate-spin" /> : "Cancel"}
                </Button>
              </div>
            )}

            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {data.plans.map((p) => {
                const isCurrent = p.tier === currentTier;
                return (
                  <Card key={p.tier} className={`border-2 transition-all ${isCurrent ? "border-brand shadow-md" : "border-slate-200"}`}>
                    <CardContent className="pt-6 space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Zap className="w-5 h-5 text-brand" />
                          <span className="text-lg font-heading font-bold text-slate-800">{p.tier}</span>
                        </div>
                        {isCurrent && <Badge className="bg-brand text-white border-0">Current</Badge>}
                      </div>
                      <p className="text-sm text-slate-600">Money in <strong>{p.days} day{p.days === 1 ? "" : "s"}</strong> after delivery.</p>
                      <div className="rounded-lg bg-slate-50 p-3">
                        <p className="text-2xl font-heading font-bold text-slate-900 tabular-nums">{p.rate_pct}%</p>
                        <p className="text-xs text-slate-500">of COD + {gst}% GST, deducted from each payout</p>
                      </div>
                      {isCurrent ? (
                        <Button variant="outline" className="w-full" disabled>
                          <Check className="w-4 h-4 mr-2" /> Active
                        </Button>
                      ) : (
                        <Button
                          className="w-full"
                          disabled={!data.eligible || !canOptIn || !!busyTier}
                          onClick={() => optIn(p.tier)}
                          data-testid={`early-cod-select-${p.tier}`}
                        >
                          {busyTier === p.tier ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Zap className="w-4 h-4 mr-2" />}
                          {currentTier ? "Switch to this" : "Activate"}
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            <p className="text-xs text-slate-400">
              You can switch or cancel anytime. The fee applies only to COD orders paid out while a plan is active.
            </p>
          </>
        )}
      </div>
    </SellerLayout>
  );
};

export default B2CEarlyCod;
